import React from 'react';
import { FinancialPlanData } from '../../types/financialPlan';
import {
  computeRetirementCorpus,
  formatCurrency,
  formatPct,
  totalRetirementSpendingToday,
} from '../../utils/lifetimeGoalsCalc';
import { grossIncomeTodayAtAge } from '../../utils/financialPlanDefaults';
import { ACCOUNT_ALLOCATION_KEYS } from '../../utils/lgcAccountAllocation';
import './LgcSharedPlanDetails.css';

const ACCOUNT_LABELS: Record<(typeof ACCOUNT_ALLOCATION_KEYS)[number], string> = {
  k401: 'SEP IRA / 401(k)',
  rothIra: 'Roth IRA',
  hsa: 'HSA',
  taxable: 'Taxable brokerage',
};

interface LgcSharedPlanDetailsProps {
  plan: FinancialPlanData;
}

const LgcSharedPlanDetails: React.FC<LgcSharedPlanDetailsProps> = ({ plan }) => {
  const { profile, retirement, income, currentFinances, children, debts, accounts } = plan;
  const corpus = computeRetirementCorpus(profile, retirement);
  const spendingToday = totalRetirementSpendingToday(retirement);
  const grossToday = grossIncomeTodayAtAge(profile, income, profile.currentAge);

  return (
    <div className="lgc-shared-details">
      <section className="lgc-shared-details__section" aria-labelledby="lgc-shared-profile">
        <h2 id="lgc-shared-profile" className="lgc-shared-details__heading">
          Profile & assumptions
        </h2>
        <dl className="lgc-shared-details__grid">
          <div>
            <dt>Current age</dt>
            <dd>{profile.currentAge}</dd>
          </div>
          <div>
            <dt>Retirement age</dt>
            <dd>{profile.retirementAge}</dd>
          </div>
          <div>
            <dt>Inflation</dt>
            <dd>{formatPct(profile.inflationPct, 1)}</dd>
          </div>
          <div>
            <dt>Portfolio return</dt>
            <dd>{formatPct(profile.portfolioReturnPct, 1)}</dd>
          </div>
          <div>
            <dt>Effective tax rate</dt>
            <dd>{formatPct(profile.effectiveTaxRatePct, 1)}</dd>
          </div>
          <div>
            <dt>Safe withdrawal rate</dt>
            <dd>{formatPct(profile.safeWithdrawalRatePct, 1)}</dd>
          </div>
        </dl>
      </section>

      <section className="lgc-shared-details__section" aria-labelledby="lgc-shared-income">
        <h2 id="lgc-shared-income" className="lgc-shared-details__heading">
          Income phases
        </h2>
        {income.phases.length === 0 ? (
          <p className="lgc-shared-details__empty">No income phases configured.</p>
        ) : (
          <div className="lgc-shared-details__table-wrap">
            <table className="lgc-shared-details__table">
              <thead>
                <tr>
                  <th scope="col">Phase</th>
                  <th scope="col">Ages</th>
                  <th scope="col">Base /yr</th>
                  <th scope="col">Bonus /yr</th>
                </tr>
              </thead>
              <tbody>
                {income.phases.map((phase) => (
                  <tr key={phase.id}>
                    <td>{phase.label || 'Phase'}</td>
                    <td>
                      {phase.startAge}–{phase.endAge}
                    </td>
                    <td className="lgc-mono">{formatCurrency(phase.grossAnnualIncome)}</td>
                    <td className="lgc-mono">
                      {phase.annualBonus ? formatCurrency(phase.annualBonus) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="lgc-shared-details__note">
          Gross income today (age {profile.currentAge}):{' '}
          <span className="lgc-mono">{formatCurrency(grossToday)}/yr</span>
        </p>
      </section>

      <section className="lgc-shared-details__section" aria-labelledby="lgc-shared-retirement">
        <h2 id="lgc-shared-retirement" className="lgc-shared-details__heading">
          Retirement goal
        </h2>
        <dl className="lgc-shared-details__grid">
          <div>
            <dt>Goal mode</dt>
            <dd>{retirement.mode === 'corpus' ? 'Target corpus' : 'Expenses-based'}</dd>
          </div>
          <div>
            <dt>Corpus at retirement</dt>
            <dd className="lgc-mono">{formatCurrency(corpus)}</dd>
          </div>
          {retirement.mode === 'expenses' && (
            <div>
              <dt>Spending today</dt>
              <dd className="lgc-mono">{formatCurrency(spendingToday)}/yr</dd>
            </div>
          )}
          <div>
            <dt>Years in retirement</dt>
            <dd>{retirement.yearsInRetirement}</dd>
          </div>
        </dl>
        {retirement.mode === 'expenses' && retirement.spendingCategories.length > 0 && (
          <div className="lgc-shared-details__table-wrap">
            <table className="lgc-shared-details__table">
              <thead>
                <tr>
                  <th scope="col">Spending category</th>
                  <th scope="col">Today /mo</th>
                </tr>
              </thead>
              <tbody>
                {retirement.spendingCategories.map((cat) => (
                  <tr key={cat.id}>
                    <td>{cat.label}</td>
                    <td className="lgc-mono">{formatCurrency(cat.annualAmountToday / 12)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="lgc-shared-details__section" aria-labelledby="lgc-shared-accounts">
        <h2 id="lgc-shared-accounts" className="lgc-shared-details__heading">
          Accounts
        </h2>
        <div className="lgc-shared-details__table-wrap">
          <table className="lgc-shared-details__table">
            <thead>
              <tr>
                <th scope="col">Account</th>
                <th scope="col">Balance</th>
                <th scope="col">Monthly contrib.</th>
              </tr>
            </thead>
            <tbody>
              {ACCOUNT_ALLOCATION_KEYS.map((key) => (
                <tr key={key}>
                  <td>{ACCOUNT_LABELS[key]}</td>
                  <td className="lgc-mono">{formatCurrency(accounts[key].balance)}</td>
                  <td className="lgc-mono">{formatCurrency(accounts[key].monthlyContribution)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="lgc-shared-details__section" aria-labelledby="lgc-shared-living">
        <h2 id="lgc-shared-living" className="lgc-shared-details__heading">
          Current finances
        </h2>
        <dl className="lgc-shared-details__grid">
          <div>
            <dt>Monthly expenses</dt>
            <dd className="lgc-mono">{formatCurrency(currentFinances.monthlyExpenses)}</dd>
          </div>
          <div>
            <dt>Tithing / giving</dt>
            <dd>{formatPct(currentFinances.tithingPct, 1)} of take-home</dd>
          </div>
          <div>
            <dt>Emergency fund target</dt>
            <dd className="lgc-mono">{formatCurrency(currentFinances.emergencyFundTarget)}</dd>
          </div>
        </dl>
      </section>

      {children.length > 0 && (
        <section className="lgc-shared-details__section" aria-labelledby="lgc-shared-education">
          <h2 id="lgc-shared-education" className="lgc-shared-details__heading">
            Education
          </h2>
          <div className="lgc-shared-details__table-wrap">
            <table className="lgc-shared-details__table">
              <thead>
                <tr>
                  <th scope="col">Child</th>
                  <th scope="col">Age</th>
                  <th scope="col">529 balance</th>
                  <th scope="col">529 /mo</th>
                </tr>
              </thead>
              <tbody>
                {children.map((child) => (
                  <tr key={child.id}>
                    <td>{child.name}</td>
                    <td>{child.currentAge}</td>
                    <td className="lgc-mono">{formatCurrency(child.balance529)}</td>
                    <td className="lgc-mono">{formatCurrency(child.monthlyContribution529)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {debts.length > 0 && (
        <section className="lgc-shared-details__section" aria-labelledby="lgc-shared-debts">
          <h2 id="lgc-shared-debts" className="lgc-shared-details__heading">
            Debts
          </h2>
          <div className="lgc-shared-details__table-wrap">
            <table className="lgc-shared-details__table">
              <thead>
                <tr>
                  <th scope="col">Debt</th>
                  <th scope="col">Balance</th>
                  <th scope="col">Payment /mo</th>
                </tr>
              </thead>
              <tbody>
                {debts.map((debt) => (
                  <tr key={debt.id}>
                    <td>{debt.label}</td>
                    <td className="lgc-mono">{formatCurrency(debt.balance)}</td>
                    <td className="lgc-mono">{formatCurrency(debt.monthlyPayment)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default LgcSharedPlanDetails;
